import { useEffect, type ReactNode } from 'react';
import { X, Monitor } from 'lucide-react';
import { BrowserMockup } from './BrowserMockup';
import { EVENT_TYPE_ICON, initials } from './liveScreenUtils';

export interface StudentScreenModalProps {
  name: string;
  email?: string;
  isOnline: boolean;
  isFlagged: boolean;
  currentDomain: string | null;
  currentPageTitle?: string | null;
  currentUrl?: string | null;
  currentEventType: string | null;
  /** Screenshot URL — if provided and valid, shown instead of mockup */
  screenshotUrl?: string | null;
  screenshotIsHarmful?: boolean;
  /** Status text shown under the name (e.g. "Online now", "Last seen …") */
  statusText: string;
  /** Dot colour class for the status indicator */
  statusDotClass: string;
  dm: boolean;
  onClose: () => void;
  /** Info cells rendered in a grid row below the screen area */
  infoCells: { label: string; value: string; colorClass?: string }[];
  /** Optional slot rendered between the screen area and info cells (e.g. admin action toolbar) */
  actions?: ReactNode;
  /** Optional slot rendered above the close button in the header (e.g. alert badge) */
  headerExtra?: ReactNode;
}

export function StudentScreenModal({
  name, email, isOnline, isFlagged,
  currentDomain, currentPageTitle, currentUrl, currentEventType,
  screenshotUrl, screenshotIsHarmful,
  statusText, statusDotClass,
  dm, onClose,
  infoCells, actions, headerExtra,
}: StudentScreenModalProps) {

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const isFlaggedOrHarmful = isFlagged || !!screenshotIsHarmful;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`relative w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl ${dm ? 'bg-slate-900' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ────────────────────────────────────────────── */}
        <div className={`flex items-center justify-between px-5 py-3 border-b ${dm ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-slate-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold ${
              isFlagged ? 'bg-red-600 text-white' : (dm ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700')
            }`}>
              {initials(name)}
            </div>
            <div>
              <p className={`font-semibold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{name}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <div className={`h-1.5 w-1.5 rounded-full ${statusDotClass}`} />
                <span className={`text-xs ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{statusText}</span>
                {headerExtra}
              </div>
              {email && <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{email}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg transition-colors ${dm ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ── Screen area ───────────────────────────────────────── */}
        <ScreenContent
          screenshotUrl={screenshotUrl}
          currentDomain={currentDomain}
          currentPageTitle={currentPageTitle}
          currentUrl={currentUrl}
          currentEventType={currentEventType}
          isOnline={isOnline}
          isFlaggedOrHarmful={isFlaggedOrHarmful}
          dm={dm}
        />

        {/* ── Actions slot ──────────────────────────────────────── */}
        {actions}

        {/* ── Info cells ────────────────────────────────────────── */}
        <div className={`grid divide-x text-center border-t ${dm ? 'border-slate-700 divide-slate-700' : 'border-slate-200 divide-slate-200'}`}
          style={{ gridTemplateColumns: `repeat(${infoCells.length}, minmax(0, 1fr))` }}
        >
          {infoCells.map(cell => (
            <div key={cell.label} className="px-4 py-3">
              <p className={`text-sm font-bold ${cell.colorClass ?? (dm ? 'text-slate-200' : 'text-slate-800')}`}>{cell.value}</p>
              <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{cell.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Screen content (screenshot or mockup) ──────────────────────── */

function ScreenContent({
  screenshotUrl, currentDomain, currentPageTitle, currentUrl, currentEventType,
  isOnline, isFlaggedOrHarmful, dm,
}: {
  screenshotUrl?: string | null;
  currentDomain: string | null;
  currentPageTitle?: string | null;
  currentUrl?: string | null;
  currentEventType: string | null;
  isOnline: boolean;
  isFlaggedOrHarmful: boolean;
  dm: boolean;
}) {
  // If a screenshot URL is provided, try showing it; BrowserMockup is the fallback.
  if (screenshotUrl) {
    return (
      <div className="relative bg-black" style={{ aspectRatio: '16/9' }}>
        <img
          src={screenshotUrl}
          alt="Screen"
          className="w-full h-full object-contain"
          onError={e => {
            // On image error, replace with mockup fallback
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        {isFlaggedOrHarmful && <FlaggedBadge />}
      </div>
    );
  }

  // No screenshot — show a large mockup (admin) or browser chrome (teacher)
  if (currentDomain && isOnline) {
    return (
      <div className={`relative ${dm ? 'bg-slate-950' : 'bg-slate-900'}`} style={{ aspectRatio: '16/9' }}>
        <div className="w-full h-full flex flex-col items-center justify-center">
          <span className="text-5xl mb-4">{EVENT_TYPE_ICON[currentEventType || ''] || '🌐'}</span>
          <p className="text-white text-xl font-semibold">{currentDomain}</p>
          {(currentPageTitle || currentUrl) && (
            <p className="text-slate-400 text-sm mt-1 max-w-md text-center px-4">{currentPageTitle || currentUrl}</p>
          )}
          <p className="text-slate-600 text-xs mt-3">No screenshot available for this session</p>
        </div>
        {isFlaggedOrHarmful && <FlaggedBadge />}
      </div>
    );
  }

  // Fallback: simple BrowserMockup
  return (
    <div className="p-4" style={{ aspectRatio: '16/9' }}>
      <BrowserMockup
        domain={currentDomain}
        pageTitle={currentPageTitle}
        url={currentUrl}
        eventType={currentEventType}
        isOnline={isOnline}
        dm={dm}
        compact={false}
      />
    </div>
  );
}

function FlaggedBadge() {
  return (
    <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-600 text-white text-sm font-bold px-3 py-1.5 rounded-lg">
      <Monitor className="h-4 w-4" />
      Flagged Content Detected
    </div>
  );
}
